import React from 'react';

export type ClientId = 'claude' | 'chatgpt' | 'gemini';

interface ClientBrand {
  label: string;
  // Each product's real brand color(s) — used only as a small accent (a dot + label) on the
  // example response, not to imitate that app's actual interface.
  accent: string;
}

export const CLIENT_BRANDS: Record<ClientId, ClientBrand> = {
  claude: { label: 'Claude', accent: '#CC785C' },
  chatgpt: { label: 'ChatGPT', accent: '#10A37F' },
  gemini: { label: 'Gemini', accent: 'linear-gradient(90deg, #4285F4, #9B72CB, #D96570)' },
};

const CLIENT_IDS = Object.keys(CLIENT_BRANDS) as ClientId[];

const styles: { [key: string]: React.CSSProperties } = {
  section: {
    width: '100%',
    maxWidth: '32rem',
  },
  exampleLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: 'var(--text-color-muted)',
    marginBottom: '0.75rem',
    textAlign: 'center',
  },
  switcher: {
    display: 'flex',
    justifyContent: 'center',
    gap: '0.375rem',
    marginBottom: '1rem',
  },
  switcherPill: {
    padding: '0.375rem 0.875rem',
    borderRadius: '999px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'rgba(45, 52, 54, 0.12)',
    background: 'transparent',
    color: 'var(--text-color-muted)',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  switcherPillActive: {
    background: 'var(--primary-color)',
    borderColor: 'var(--primary-color)',
    color: 'var(--white-color)',
  },
  chatBubbleRow: {
    display: 'flex',
    marginBottom: '0.75rem',
  },
  userBubble: {
    marginLeft: 'auto',
    maxWidth: '85%',
    background: '#F5F7FA',
    color: 'var(--primary-text-color)',
    borderRadius: '1rem 1rem 0.25rem 1rem',
    padding: '0.75rem 1rem',
    fontSize: '0.9375rem',
  },
  assistantBubble: {
    marginRight: 'auto',
    maxWidth: '85%',
    background: 'var(--white-color)',
    boxShadow: '0 4px 24px rgba(45, 52, 54, 0.08)',
    borderRadius: '1rem 1rem 1rem 0.25rem',
    padding: '0.875rem 1.125rem',
    fontSize: '0.9375rem',
    lineHeight: 1.5,
  },
  assistantHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    marginBottom: '0.5rem',
    fontSize: '0.8125rem',
    fontWeight: 600,
  },
  assistantDot: {
    width: '0.5rem',
    height: '0.5rem',
    borderRadius: '50%',
    flexShrink: 0,
  },
};

// A solid brand color becomes plain text color; a CSS gradient (Gemini) is clipped to the text
// instead, since `color` alone can't render a gradient.
const accentTextStyle = (accent: string): React.CSSProperties =>
  accent.startsWith('linear-gradient')
    ? { background: accent, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }
    : { color: accent };

interface ChatExampleProps {
  client: ClientId;
  // When provided, a Claude / ChatGPT / Gemini switcher replaces the plain "Example" label —
  // for pages that don't already have client tabs driving which one is shown.
  onClientChange?: (client: ClientId) => void;
  style?: React.CSSProperties;
}

const ChatExample: React.FC<ChatExampleProps> = ({ client, onClientChange, style }) => {
  const brand = CLIENT_BRANDS[client];

  return (
    <div style={{ ...styles.section, ...style }}>
      {onClientChange ? (
        <div style={styles.switcher} role="tablist" aria-label="Example AI assistant">
          {CLIENT_IDS.map((id) => (
            <button
              key={id}
              role="tab"
              aria-selected={client === id}
              style={{ ...styles.switcherPill, ...(client === id ? styles.switcherPillActive : {}) }}
              onClick={() => onClientChange(id)}
            >
              {CLIENT_BRANDS[id].label}
            </button>
          ))}
        </div>
      ) : (
        <div style={styles.exampleLabel}>Example</div>
      )}

      <div style={styles.chatBubbleRow}>
        <div style={styles.userBubble}>
          Using Stashr, tell me which credit card in my wallet I should use when buying from
          Target.com
        </div>
      </div>

      <div style={styles.chatBubbleRow}>
        <div style={styles.assistantBubble}>
          <div style={styles.assistantHeader}>
            <span style={{ ...styles.assistantDot, background: brand.accent }} />
            <span style={accentTextStyle(brand.accent)}>{brand.label}</span>
          </div>
          Looking at the cards you've added, your <strong>Chase Freedom Flex</strong> earns 5%
          back at Target this quarter through its rotating bonus category, while your other cards
          top out around 1.5–2%. I'd use the Freedom Flex for this one — roughly $2.50 back on a
          $50 order versus about $1 with your other cards.
        </div>
      </div>
    </div>
  );
};

export default ChatExample;
